import { Suspense } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { IconRail } from './IconRail';
import { LowBalanceBanner } from './LowBalanceBanner';
import { PageCanvas } from './PageCanvas';
import { SessionDrawer } from './SessionDrawer';
import { SessionDrawerPresenceProvider } from './sessionDrawerPresence';

/**
 * Skip link (2026-08-19 remediation Slice B, Task 4): the icon rail carries
 * 10 stops before the page content, so a keyboard/screen-reader user pays
 * that cost on every single navigation. Visually hidden until focused
 * (`sr-only` / `focus-visible:not-sr-only`), and MUST be the first
 * focusable element in the tree, so it renders before `IconRail`, not after.
 *
 * `preventDefault` + an explicit `.focus()` call, rather than relying on the
 * bare `href="#main"` fragment navigation: native fragment-focus behaviour
 * for a `tabIndex={-1}` target is inconsistent across browsers, and letting
 * the default action through would also push a `#main` hash into the URL.
 * `Element.focus()` already scrolls the target into view itself, so no
 * separate `scrollIntoView` call is needed.
 */
function SkipLink() {
  return (
    <a
      href="#main"
      onClick={(e) => {
        e.preventDefault();
        document.getElementById('main')?.focus();
      }}
      className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:left-4 focus-visible:top-4 focus-visible:z-50 focus-visible:rounded focus-visible:bg-teal focus-visible:px-4 focus-visible:py-2 focus-visible:text-teal-ink"
    >
      Sari la conținut
    </a>
  );
}

/** Direction B: 64px icon rail + a centered document canvas. No top bar. */
export function AppShell() {
  // Dense tabular review — the Mapări results table (/mapari/:id/tabel) AND the
  // validation wizard (/mapari/:id/validare + steps) — opts out of the 1120px
  // document canvas into full width (DESIGN.md sanctioned). Both are needed:
  // MaparePage renders the table on /tabel and the wizard on /validare*. /export
  // and /noua stay on the document canvas, so they're deliberately not matched.
  const inValidare = useMatch({ path: '/mapari/:id/validare', end: false });
  const inTabel = useMatch('/mapari/:id/tabel');
  const fullWidth = Boolean(inValidare || inTabel);
  return (
    <TooltipProvider delayDuration={200}>
      <SessionDrawerPresenceProvider>
        <div className="flex h-full flex-col md:flex-row">
          <SkipLink />
          <IconRail />
          {/* overflow-x-hidden: this column is the vertical scroll container
              (overflow-y-auto), and CSS promotes its overflow-x to `auto` too —
              so when the vertical scrollbar claims its gutter, content overflows
              by that sliver and a stray page-level horizontal scrollbar appears.
              Clipping x kills that artifact; wide tables keep their OWN
              overflow-x-auto scroll, so nothing real is hidden. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
            <LowBalanceBanner />
            <main id="main" tabIndex={-1} className="w-full px-4 py-6 md:px-8 md:py-8">
              <PageCanvas width={fullWidth ? 'full' : 'document'}>
                {/* Boundary for the lazy-loaded routes (MapareNouaPage/ExportPage,
                    which carry the heavy file libs). fallback=null: navigation is
                    near-instant on a warm bundle and a spinner would only flash. */}
                <Suspense fallback={null}>
                  <Outlet />
                </Suspense>
              </PageCanvas>
            </main>
          </div>
          <SessionDrawer />
        </div>
      </SessionDrawerPresenceProvider>
    </TooltipProvider>
  );
}
